import React, { useEffect, useState, useCallback } from "react"
import FullTable from "components/shared/FullTable"
import {
	useCreateEntriesMutation,
	useMainRetrieveQuery,
	useModifyEntryMutation,
	useMainPatchMutation,
} from "services/api"
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik"
import helpers from "helpers"
import FormError from "components/shared/FormError"
import Select from "components/Select"
import * as yup from "yup"
import Matrix from "components/Matrix"
import { toast } from "react-toastify"
import Modal from "react-responsive-modal"
import NumberField from "components/shared/NumberField"
import DeleteUndoButton from "./DeleteUndoButton"
import _ from "lodash"
import moment from "moment"
import classNames from "classnames"
import { futureStatusValidation, isFutureSection } from "./entryValidation"

function Entry() {
	const q = useMainRetrieveQuery({ action: "getEntries", tags: ["entries"] })
	const sections = useMainRetrieveQuery({ action: "getSections", tags: ["sections"] })
	const communities = useMainRetrieveQuery({ action: "getCommunities", tags: ["communities"] })
	const builders = useMainRetrieveQuery({ action: "getBuilders", tags: ["builders"] })
	const subdivisions = useMainRetrieveQuery({ action: "getSubdivisions", tags: ["subdivisions"] })
	const submarkets = useMainRetrieveQuery({ action: "getSubmarkets", tags: ["submarkets"] })
	const [create, createData] = useCreateEntriesMutation({
		refetchQueries: ["MainRetrieve"],
	})
	const [modify, createModify] = useModifyEntryMutation()
	const [entries, setEntries] = useState([])
	const [editData, setEditData] = useState(null)
	const [subdivision, setSubdivision] = useState(null)
	const [patch, patchData] = useMainPatchMutation()
	const [confirmDelete, setConfirmDelete] = useState(false)
	const [entryQuarter, setEntryQuarter] = useState(moment().startOf("quarter").unix())
	const [editModal, setEditModal] = useState(false)
	const [setupData, setSetupData] = useState(null) // This is when they select a sub, it has to build the lot type + builder + num of lots
	const [hasBTR, setHasBTR] = useState(false)
	const [setupReady, setSetupReady] = useState(false)
	const [selectedEntries, setSelectedEntries] = useState([])
	const [cloneModal, setCloneModal] = useState(false)
	const [deleteModal, setDeleteModal] = useState(false)
	const [bulkDeleting, setBulkDeleting] = useState(false)
	const [selectedQuarter, setSelectedQuarter] = useState(null)
	const [filteredEntryIds, setFilteredEntryIds] = useState([])
	let model = {
		section: "",
		occupied: "",
		models: "",
		complete_vacant: "",
		under_construction: "",
		vdl: "",
		futures: "",
		total: "",
		builder: "",
		lot_type: "",
		num_lots: "",
		subdivision: "",
		quarter: moment().startOf("quarter").unix(),
		lot_types_builders: [],
	}

	useEffect(() => {
		//console.log("Updating Entries...")
		if (q.data) {
			const builtRows = buildRows(q.data)
			setEntries(builtRows)
			setSelectedEntries((prev) => prev.filter((id) => builtRows.some((entry) => entry.id === id)))
		}
	}, [sections.isSuccess, communities.isSuccess, builders.isSuccess, subdivisions.isSuccess, q.status])

	useEffect(() => {
		if (!Array.isArray(setupData)) return
		let btr = false
		_.forEach(setupData, (v) => {
			let section = _.find(sections?.data, { id: v.section })
			if (section?.btr) btr = true
		})
		if (btr !== hasBTR) setHasBTR(btr)
	}, [setupData])

	const buildRows = (data) => {
		let ret = []
		_.forEach(data, (item) => {
			ret.push({
				...item,
				edit: (
					<i
						className="fa-solid fa-pen-field cursor-pointer text-primary"
						onClick={() => {
							setEditData(item)
							setEditModal(true)
						}}></i>
				),
			})
		})

		return ret
	}

	const validation = () => {
		return yup.object().shape({
			section: yup.number().required("Required"),
			quarter: yup.number().required("Required"),
			observed_status: futureStatusValidation(),
		})
	}

	const handleSelectAll = (checked) => {
		if (checked) {
			// Select all filtered entries
			setSelectedEntries(filteredEntryIds)
		} else {
			setSelectedEntries([])
		}
	}

	const handleSelectEntry = (id, checked) => {
		if (checked) {
			setSelectedEntries(prev => [...prev, id])
		} else {
			setSelectedEntries(prev => prev.filter(entryId => entryId !== id))
		}
	}

	const submarketNameById = React.useMemo(() => {
		const map = new Map()
		if (!submarkets?.data) return map
		submarkets.data.forEach((item) => {
			map.set(item.id, item.name)
		})
		return map
	}, [submarkets?.data])

	const submarketBySubdivisionId = React.useMemo(() => {
		const map = new Map()
		if (!subdivisions?.data) return map
		subdivisions.data.forEach((item) => {
			const name = item.submarketName || item.submarket_name || submarketNameById.get(item.submarket) || ""
			if (name) {
				map.set(item.id, name)
			}
		})
		return map
	}, [subdivisions?.data, submarketNameById])

	// Calculate the count of selected entries that are currently visible in the filtered view
	const getFilteredSelectedCount = () => {
		return selectedEntries.filter(id => filteredEntryIds.includes(id)).length
	}

	// Get only the selected entries that are in the filtered view
	const getFilteredSelectedEntries = () => {
		return selectedEntries.filter(id => filteredEntryIds.includes(id))
	}

	const getFilteredSelectedEntryRows = () => {
		const ids = new Set(getFilteredSelectedEntries())
		return entries.filter((entry) => ids.has(entry.id))
	}

	const entryDeleteLabel = (entry) => {
		const counts = [
			entry.futures ? `Futures ${entry.futures}` : null,
			entry.under_construction ? `UC ${entry.under_construction}` : null,
			entry.vdl ? `VDL ${entry.vdl}` : null,
			entry.complete_vacant ? `FV ${entry.complete_vacant}` : null,
			entry.models ? `Models ${entry.models}` : null,
			entry.occupied ? `Occupied ${entry.occupied}` : null,
		].filter(Boolean)

		return [
			`ID ${entry.id}`,
			entry.quarter ? moment.unix(entry.quarter).format("[Q]Q YYYY") : null,
			entry.sectionName || (entry.section ? `Section ${entry.section}` : null),
			entry.subdivision ? helpers.translateID("subdivision", entry, subdivisions) : null,
			entry.builder ? helpers.translateID("builder", entry, builders) : null,
			entry.lot_type ? `Lot ${entry.lot_type}` : null,
			counts.length ? counts.join(", ") : null,
		]
			.filter(Boolean)
			.join(" | ")
	}

	const handleRestoreDeleteBatch = async (undoId) => {
		const resp = await patch({
			action: "restoreDeleteBatch",
			id: undoId,
			tags: ["entries", "builders", "developers", "deleteUndo"],
		})

		if (resp?.data?.status === 200 && !resp?.data?.message?.includes("not restored")) {
			toast.success(resp?.data?.message || "Delete batch restored.")
		} else {
			toast.error(resp?.data?.message || "Unable to restore delete batch.")
		}
	}

	const handleBulkDelete = async () => {
		const filteredSelectedIds = getFilteredSelectedEntries()
		if (!filteredSelectedIds.length) {
			toast.warning("No entries selected in current view")
			return
		}
		setBulkDeleting(true)
		try {
			const resp = await patch({
				action: "deleteEntries",
				entryIds: filteredSelectedIds,
				tags: ["entries", "deleteUndo"],
			})

			if (resp?.data?.status === 200) {
				const message = resp?.data?.message
				const undoId = message?.undoId
				toast.success(
					<div>
						<span>{message?.text || `Deleted ${filteredSelectedIds.length} entries.`}</span>
						{undoId ? (
							<button
								type="button"
								className="btn !text-xs !py-1 !px-2 ml-3"
								onClick={() => handleRestoreDeleteBatch(undoId)}>
								Undo
							</button>
						) : null}
					</div>,
					{ autoClose: 10000 }
				)
				setSelectedEntries([])
				setDeleteModal(false)
			} else {
				toast.error(resp?.data?.message || "Error deleting entries")
			}
		} catch (error) {
			console.error("Bulk delete error:", error)
			toast.error("An error occurred while deleting entries")
		}
		setBulkDeleting(false)
	}

	// Reusable footer total calculator
	const createFooterTotal = (fieldName) => {
		return (info) => {
			const total = info.table.getFilteredRowModel().rows.reduce((sum, row) => {
				const value = row.original[fieldName]
				return sum + (Number(value) || 0)
			}, 0)
			return <strong>{total.toLocaleString()}</strong>
		}
	}

	// Memoize columns but make select checkbox reactive by accessing state in the render
	const columns = React.useMemo(
		() => [
			{
				id: "select",
				header: "Select",
				cell: (info) => (
					<input
						type="checkbox"
						checked={selectedEntries.includes(info.row.original.id)}
						onChange={(e) => handleSelectEntry(info.row.original.id, e.target.checked)}
					/>
				),
				filterable: false,
			},
			{
				id: "id",
				header: "ID",
			},
			{
				id: "quarter",
				header: "Quarter",
				accessorFn: (row) => {
					if (row.quarter) return `Q${moment.unix(row.quarter).format("Q YYYY")}`
					return ""
				},
			},
			{
				id: "sectionName",
				header: "Section",
				//accessorFn: (row) => helpers.translateID("section", row, sections),
			},
			{
				id: "lot_type",
				header: "Lot Size",
				cell: (v) => {
					let val = v.getValue()
					if (val > 1000) {
						let ret = helpers.translateLargeLotTypes(val)
						return ret
					}
					return val
				},
			},
			{
				id: "builder",
				header: "Builder",
				accessorFn: (row) => helpers.translateID("builder", row, builders),
			},
			{
				id: "subdivision",
				header: "Subdivision",
				accessorFn: (row) => helpers.translateID("subdivision", row, subdivisions),
			},
			{
				id: "submarket",
				header: "Submarket",
				accessorFn: (row) => submarketBySubdivisionId.get(row.subdivision) || "",
			},
			{
				id: "public_name",
				header: "Public Name",
				accessorFn: (row) => helpers.colPublicName(row, subdivisions),
			},
			{
				id: "community",
				header: "MPC",
				accessorFn: (row) => helpers.translateID("community", row, communities),
			},
			{
				id: "futures",
				header: "Futures",
				footer: createFooterTotal("futures"),
			},
			{
				id: "under_construction",
				header: "Under Construction",
				footer: createFooterTotal("under_construction"),
			},
			{
				id: "vdl",
				header: "VDL",
				footer: createFooterTotal("vdl"),
			},
			{
				id: "complete_vacant",
				header: "Finished Vacant",
				footer: createFooterTotal("complete_vacant"),
			},
			{
				id: "models",
				header: "Models",
				footer: createFooterTotal("models"),
			},
			{
				id: "occupied",
				header: "Occupied",
				footer: createFooterTotal("occupied"),
			},
			{
				id: "edit",
				header: "Edit",
				filterable: false,
			},
		],
		[selectedEntries, submarketBySubdivisionId]
	)

	const buildBuilders = (f) => {
		if (!builders?.data) return []
		const allBuilders = helpers.buildOpts(builders)
		if (!f.values.section) return allBuilders

		const section = _.find(sections?.data, { id: f.values.section })
		let lotTypesBuilders = section?.lot_types_builders

		// lot_types_builders may be persisted as JSON string; parse defensively
		if (typeof lotTypesBuilders === "string") {
			try {
				lotTypesBuilders = JSON.parse(lotTypesBuilders)
			} catch (e) {
				lotTypesBuilders = []
			}
		}

		const sectionBuilderIds = new Set(lotTypesBuilders?.map((r) => Number(r.builder)))
		const sectionOptions = allBuilders.filter((b) => sectionBuilderIds.has(Number(b.value)))

		// Always include full list, but put section-mapped builders first when available
		return _.uniqBy([...sectionOptions, ...allBuilders], "value")
	}

	const buildSubdivisions = () => {
		let ret = []
		if (!subdivisions?.data) return []
		_.forEach(subdivisions.data, (item) => {
			let label = `${item.name} | (${item.public_name})`
			if (item.communityName) label += ` - ${item.communityName}`
			if (item.developerName) label += ` - ${item.developerName}`
			if (item.submarketName) label += ` - ${item.submarketName}`
			if (item.city) label += ` - ${item.city}`

			ret.push({ value: item.id, label })
		})
		return ret
	}

	const handleFilteredRowsChange = useCallback((filteredRows) => {
		setFilteredEntryIds(filteredRows.map(row => row.id))
	}, [])

	const DisplayForm = (props) => {
		let init = _.clone(model)
		if (editData && !props?.new) init = editData
		return (
			<Formik
				initialValues={init}
				validationSchema={validation}
				onSubmit={async (values, { resetForm, setSubmitting }) => {
					const body = new FormData()
					_.forEach(values, (v, k) => {
						if (v === "null") return
						body.append(k, v)
					})
					body.append("type", "entries")
					body.append("action", "createEntries")
					body.append("tags", ["entries"])
					let resp = await modify(body)
					if (resp.data?.status === 200) {
						toast.success(resp.data.message)
						resetForm()
						setEditData(null)
						setEditModal(false)
					} else if (resp.data?.status === 400) {
						toast.error("Error creating user.")
					}
					setSubmitting(false)
				}}>
				{(f) => (
					<>
						<Form>
							{/* console.log(f) */}
							<ul className="formList">
								<li className={helpers.formField(f, "quarter")}>
									<ErrorMessage name="quarter" component={FormError} className="text-red-500" />
									<label>Quarter</label>
									<Select
										name="quarter"
										value={f.values.quarter}
										onChange={f.setFieldValue}
										options={helpers.buildQuarters({ allTime: editData ? true : false })}
										onBlur={f.setFieldTouched}
									/>
								</li>
								<li className={helpers.formField(f, "section")}>
									<ErrorMessage name="section" component={FormError} className="text-red-500" />
									<label>Section</label>
									<Select
										name="section"
										value={f.values.section}
										onChange={f.setFieldValue}
										options={helpers.buildOpts(sections, true, { foo: true})}
										onBlur={f.setFieldTouched}
									/>
								</li>
								<li className={helpers.formField(f, "lot_type")}>
									<ErrorMessage name="lot_type" component={FormError} className="text-red-500" />
									<label>Lot Size</label>
									<Select
										name="lot_type"
										value={f.values.lot_type}
										onChange={f.setFieldValue}
										options={helpers.buildLotTypes()}
										onBlur={f.setFieldTouched}
									/>
								</li>
								<li className={helpers.formField(f, "builder")}>
									<ErrorMessage name="builder" component={FormError} className="text-red-500" />
									<label>Builder</label>
									<Select
										name="builder"
										value={f.values.builder}
										onChange={f.setFieldValue}
										options={buildBuilders(f)}
										onBlur={f.setFieldTouched}
									/>
								</li>
								<li className={helpers.formField(f, "occupied")}>
									<ErrorMessage name="occupied" component={FormError} />
									<label>Occupied</label>
									<NumberField
										name="occupied"
										type="text"
										className="form__input"
										onChange={f.setFieldValue}
										onBlur={f.setFieldTouched}
										touched={f.touched.occupied}
										error={f.errors.occupied}
										value={f.values.occupied}
										thousandSeparator={true}
										numeric={true}
									/>
								</li>
								<li className={helpers.formField(f, "models")}>
									<ErrorMessage name="models" component={FormError} />
									<label>Models</label>
									<NumberField
										name="models"
										type="text"
										className="form__input"
										onChange={f.setFieldValue}
										onBlur={f.setFieldTouched}
										touched={f.touched.models}
										error={f.errors.models}
										value={f.values.models}
										thousandSeparator={true}
										numeric={true}
									/>
								</li>
								<li className={helpers.formField(f, "complete_vacant")}>
									<ErrorMessage name="complete_vacant" component={FormError} />
									<label>Finished Vacant</label>
									<NumberField
										name="complete_vacant"
										type="text"
										className="form__input"
										onChange={f.setFieldValue}
										onBlur={f.setFieldTouched}
										touched={f.touched.complete_vacant}
										error={f.errors.complete_vacant}
										value={f.values.complete_vacant}
										thousandSeparator={true}
										numeric={true}
									/>
								</li>
								<li className={helpers.formField(f, "under_construction")}>
									<ErrorMessage name="under_construction" component={FormError} />
									<label>Under Construction</label>
									<NumberField
										name="under_construction"
										type="text"
										className="form__input"
										onChange={f.setFieldValue}
										onBlur={f.setFieldTouched}
										touched={f.touched.under_construction}
										error={f.errors.under_construction}
										value={f.values.under_construction}
										thousandSeparator={true}
										numeric={true}
									/>
								</li>
								<li className={helpers.formField(f, "vdl")}>
									<ErrorMessage name="vdl" component={FormError} />
									<label>VDL</label>
									<NumberField
										name="vdl"
										type="text"
										className="form__input"
										onChange={f.setFieldValue}
										onBlur={f.setFieldTouched}
										touched={f.touched.vdl}
										error={f.errors.vdl}
										value={f.values.vdl}
										thousandSeparator={true}
										numeric={true}
									/>
								</li>
								<li className={helpers.formField(f, "futures")}>
									<ErrorMessage name="futures" component={FormError} />
									<label>Futures</label>
									<NumberField
										name="futures"
										type="text"
										className="form__input"
										onChange={f.setFieldValue}
										onBlur={f.setFieldTouched}
										touched={f.touched.futures}
										error={f.errors.futures}
										value={f.values.futures}
										thousandSeparator={true}
										numeric={true}
									/>
								</li>
								<li className={helpers.formField(f, "total")}>
									<ErrorMessage name="total" component={FormError} />
									<label>Total</label>
									<NumberField
										name="total"
										type="text"
										className="form__input"
										onChange={f.setFieldValue}
										onBlur={f.setFieldTouched}
										touched={f.touched.total}
										error={f.errors.total}
										value={f.values.total}
										thousandSeparator={true}
										numeric={true}
									/>
								</li>
								<li className={helpers.formField(f, "observed_status")}>
									<ErrorMessage
										name="observed_status"
										component={FormError}
										className="text-red-500"
									/>
									<label>Observed Status</label>
									<Select
										name="observed_status"
										value={f.values.observed_status}
										onChange={f.setFieldValue}
										options={helpers.observedStatuses()}
										onBlur={f.setFieldTouched}
										clearable
									/>
								</li>
								<li className={helpers.formField(f, "pdf")}>
									<ErrorMessage name="pdf" component={FormError} className="text-red-500" />
									<label>
										PDF{" "}
										{f.values.pdf ? (
											<a
												href={`https://cbas-primary.s3.us-east-2.amazonaws.com${f.values.pdf}`}
												target="_blank"
												className="text-xs text-darkBlue pl-4 font-bold">
												Current Scan
											</a>
										) : null}
									</label>
									<input
										name="pdf"
										type="file"
										className="form__input"
										onChange={(event) => {
											f.setFieldValue("pdf", event.currentTarget.files[0])
										}}
									/>
								</li>
							</ul>
							<button type="submit" disabled={f.isSubmitting} className="mt-12">
								Submit
							</button>
						</Form>
					</>
				)}
			</Formik>
		)
	}

	const BuildSetup = (f) => {
		if (!setupReady && subdivision) {
			let d = []
			let secs = _.filter(sections?.data, { subdivision: subdivision })

			secs = _.map(secs, (v) => {
				return {
					label: v.name,
					value: v.id,
				}
			})
			return (
				<div>
					<h3>Lot Sizes &amp; Builders</h3>
					<Matrix
						delete={true}
						initRow={true}
						fields={[
							{
								id: "section",
								name: "Section",
								type: "select",
								applyAll: true,
								options: secs,
							},
							{
								id: "lot_type",
								name: "Lot Size",
								type: "select",
								applyAll: true,
								options: helpers.buildLotTypes(),
							},
							{
								id: "builder",
								name: "Builder",
								type: "select",
								applyAll: true,
								options: helpers.buildOpts(builders),
							},
							{
								id: "num_lots",
								name: "Number of Lots",
								type: "number",
							},
						]}
						data={d}
						onChange={(v) => (d = v)}
					/>
					<div
						className="btn inline-block mt-5"
						onClick={() => {
							setSetupData(d)
							setSetupReady(true)
						}}>
						Add Setup Data
					</div>
				</div>
			)
		}
		return ""
	}

	const BuildTable = (f) => {
		if (!subdivision || !sections || !setupReady) return null
		let init = []
		_.forEach(setupData, (v) => {
			let builder = _.find(builders?.data, { id: v.builder })
			let section = _.find(sections?.data, { id: v.section })
			init.push({
				section: v.section,
				sectionName: section?.name || "",
				builder: v.builder,
				builderName: builder?.name || "Unreleased",
				lot_type: v.lot_type,
				num_lots: v.num_lots,
				subdivision,
				quarter: moment().startOf("quarter").unix(),
				observed_status: "",
				occupied: "",
				models: "",
				complete_vacant: "",
				under_construction: "",
				vdl: "",
				btr: "",
				futures: "",
				isFuture: isFutureSection(v, section),
			})
		})
		init = helpers.customSort(init, "sectionName")
		const tableHasBTR = _.some(setupData, (v) => _.find(sections?.data, { id: v.section })?.btr)

		const blurCheck = (f) => {
			// When a number is blurred, this check runs to see if the fields equal out to the total
			_.forEach(f.values, (v, k) => {
				let orig = _.cloneDeep(v)
				let fields = ["complete_vacant", "futures", "models", "occupied", "under_construction", "vdl", "btr"]
				// Translate "" to 0's
				_.forEach(fields, (r) => {
					if (!orig[r]) orig[r] = 0
				})
				// Calculate total
				let total =
					orig.complete_vacant +
					orig.futures +
					orig.models +
					orig.occupied +
					orig.btr +
					orig.under_construction +
					orig.vdl
				orig.tracker = total - orig.num_lots
				v.tracker = orig.tracker
				f.setFieldValue(k, v)
				//arrayHelpers.replace(k, v)
			})
		}

		const Tracker = (props) => {
			let f = props.f
			let total = f.values[props.k].tracker
			return <span className={total === 0 ? "text-green-500" : "text-red-500"}>{total}</span>
		}

		const Rows = (props) => {
			let f = props.f
			let arrayHelpers = props.helpers
			let rows = []
			_.forEach(f.values, (v, k) => {
				let builder = _.find(builders?.data, { id: v.builder })
				let section = _.find(sections?.data, { id: v.section })
				const isFuture = isFutureSection(v, section)
				rows.push(
					<tr className="child:text-xs child:text-left" key={`section-${v.section}-${v.builder}-${k}`}>
						<td>{section?.name}</td>
						<td>{builder?.name || "Unreleased"}</td>
						<td>{v.lot_type}</td>
						<td>
							{!isFuture && (
								<NumberField
									name={`${k}.models`}
									type="text"
									className="form__input"
									onChange={f.setFieldValue}
									value={f.values[k]?.models}
									numeric={v.float ? false : true}
									onBlur={() => blurCheck(f, arrayHelpers)}
									float={v.float || null}
									decimals={v.decimals || null}
								/>
							)}
						</td>
						<td>
							{!isFuture && (
								<NumberField
									name={`${k}.occupied`}
									type="text"
									className="form__input"
									onChange={f.setFieldValue}
									value={f.values[k]?.occupied}
									numeric={v.float ? false : true}
									onBlur={() => blurCheck(f, arrayHelpers)}
									float={v.float || null}
									decimals={v.decimals || null}
								/>
							)}
						</td>
						<td>
							{!isFuture && (
								<NumberField
									name={`${k}.complete_vacant`}
									type="text"
									className="form__input"
									onChange={f.setFieldValue}
									value={f.values[k]?.complete_vacant}
									numeric={v.float ? false : true}
									onBlur={() => blurCheck(f, arrayHelpers)}
									float={v.float || null}
									decimals={v.decimals || null}
								/>
							)}
						</td>
						<td>
							{!isFuture && (
								<NumberField
									name={`${k}.under_construction`}
									type="text"
									className="form__input"
									onChange={f.setFieldValue}
									value={f.values[k]?.under_construction}
									numeric={v.float ? false : true}
									onBlur={() => blurCheck(f, arrayHelpers)}
									float={v.float || null}
									decimals={v.decimals || null}
								/>
							)}
						</td>
						<td>
							{!isFuture && (
								<NumberField
									name={`${k}.vdl`}
									type="text"
									className="form__input"
									onChange={f.setFieldValue}
									value={f.values[k]?.vdl}
									numeric={v.float ? false : true}
									onBlur={() => blurCheck(f, arrayHelpers)}
									float={v.float || null}
									decimals={v.decimals || null}
								/>
							)}
						</td>
						{tableHasBTR && (
							<td>
								{section?.btr && !isFuture && (
									<NumberField
										name={`${k}.btr`}
										type="text"
										className="form__input"
										onChange={f.setFieldValue}
										value={f.values[k]?.btr}
										numeric={v.float ? false : true}
										onBlur={() => blurCheck(f, arrayHelpers)}
										float={v.float || null}
										decimals={v.decimals || null}
									/>
								)}
							</td>
						)}
						<td>
							<NumberField
								name={`${k}.futures`}
								type="text"
								className="form__input"
								onChange={f.setFieldValue}
								value={f.values[k]?.futures}
								numeric={v.float ? false : true}
								onBlur={() => blurCheck(f, arrayHelpers)}
								float={v.float || null}
								decimals={v.decimals || null}
							/>
						</td>
						<td>
							<Select
								name={`${k}.observed_status`}
								value={f.values[k]?.observed_status}
								onChange={f.setFieldValue}
								options={helpers.observedStatuses()}
								onBlur={f.setFieldTouched}
								menuPortalTarget={typeof document !== "undefined" ? document.body : null}
								menuPosition="fixed"
								styles={{
									menuPortal: (base) => ({ ...base, zIndex: 9999 }),
								}}
								clearable
							/>
						</td>
						<td>
							{!isFuture && (
								<input
									name={`${k}.pdf`}
									type="file"
									onChange={(event) => {
										f.setFieldValue(`${k}.pdf`, event.currentTarget.files[0])
									}}
								/>
							)}
						</td>
						<td>
							{!isFuture && (
								<>
									{v.num_lots} / <Tracker f={f} k={k} />
								</>
							)}
						</td>
					</tr>
				)
			})
			return rows
		}

		const validation = () => {
			return yup.array().of(
				yup.object().shape({
					occupied: yup.number().nullable().default(0),
					models: yup.number().nullable().default(0),
					complete_vacant: yup.number().nullable().default(0),
					under_construction: yup.number().nullable().default(0),
					vdl: yup.number().nullable().default(0),
					futures: yup.number().nullable().default(0),
					observed_status: futureStatusValidation(),
					isFuture: yup.boolean(),
					num_lots: yup.number().nullable().when("isFuture", {
						is: true,
						then: (schema) => schema.nullable().notRequired(),
						otherwise: (schema) => schema.required(),
					}),
					tracker: yup.number().test("total-matches", "Total must match number of lots", function (value) {
						if (this.parent.isFuture) return true

						const fields = [
							"complete_vacant",
							"futures",
							"models",
							"occupied",
							"under_construction",
							"vdl",
							"btr",
						]

						const total = fields.reduce((sum, field) => {
							const fieldValue = this.parent[field]
							return sum + (fieldValue || 0)
						}, 0)

						return total === this.parent.num_lots
					}),
				})
			)
		}
		return (
			<Formik
				initialValues={init}
				validationSchema={validation}
				validateOnMount={true}
				onSubmit={async (values, { resetForm, setSubmitting, isValid }) => {
					// Go through the values and ensure the quarter is set to the ref's quarter
					values = _.map(values, (v) => {
						v.quarter = entryQuarter
						return v
					})
					const body = new FormData()
					_.forEach(values, (v, k) => {
						_.forEach(v, (r, i) => {
							body.append(`${k}[${i}]`, r)
						})
					})
					body.append("type", "entries")
					body.append("action", "createEntries")
					body.append("tags", ["entries"])
					let resp = await create(body)
					if (resp.data?.status === 200) {
						toast.success(resp.data.message)
						resetForm()
						setEditData(null)
						setEditModal(false)
						setSubdivision(null)
					} else if (resp.data?.status === 400) {
						toast.error("Error creating user.")
					}
					setSubmitting(false)
				}}>
				{(f) => (
					<Form>
						{/* console.log(f) */}
						<FieldArray
							name="sections"
							render={(arrayHelpers) => (
								<div className="overflow-x-auto">
									<table className="EntryTable min-w-[1120px] w-full">
										<thead>
											<tr className="child:text-[11px] child:text-left child:bg-neutral-200 child:py-2 child:xl:px-2">
												<th className="w-[6%]">Section</th>
												<th className="w-[15%]">Builder</th>
												<th className="w-[6%]">Lot Size</th>
												<th className="w-[3%]">Models</th>
												<th className="w-[3%]">Occupied</th>
												<th className="w-[3%]">Finished Vacant</th>
												<th className="w-[8%]">UC</th>
												<th className="w-[4%]">VDL</th>
												{tableHasBTR && <th className="w-[4%]">BTR</th>}
												<th className="w-[8%]">Futures</th>
												<th className="w-[16%]">Future Status</th>
												<th className="w-[12%]">PDF</th>
												<th className="w-[5%]">Total</th>
											</tr>
										</thead>
										<tbody>{<Rows f={f} />}</tbody>
									</table>
								</div>
							)}
						/>
						<button
							type="submit"
							className={classNames(`mt-12`, {
								"!bg-transparent border border-darkBlue !text-darkBlue opacity-50": !f.isValid,
							})}>
							Submit
						</button>
					</Form>
				)}
			</Formik>
		)
	}
	return (
		<div className="">
			<div className="group">
				<h2>Add Entries</h2>
				<div className="flex mb-5 items-center w-full">
					<div className="flex items-center w-4/12">
						<p className="text-darkBlue text-sm mr-3">Quarter:</p>
						<Select
							name="quarter"
							className="smallText w-full"
							value={entryQuarter}
							onChange={(k, v) => {
								setEntryQuarter(v)
							}}
							options={helpers.buildQuarters()}
							onBlur={() => {}}
						/>
					</div>
					<div className="flex items-center w-full pl-4 z-[200] relative">
						<p className="text-darkBlue text-sm mr-3">Subdivision:</p>
						<Select
							name="subdivision"
							className="smallText w-full"
							value={subdivision}
							onChange={(k, v) => {
								setSubdivision(v)
								setSetupData(null)
								setHasBTR(false)
								setSetupReady(false)
							}}
							clearable
							options={buildSubdivisions()}
							onBlur={() => {}}
						/>
					</div>
				</div>
				<BuildSetup />
				<BuildTable />
			</div>
			<div className="flex justify-between items-center mt-12 mb-4">
				<div className="flex items-center gap-4">
					<h2 className="!m-0">Current Entries</h2>
					<label className="flex items-center gap-2 text-sm cursor-pointer">
						<input
							type="checkbox"
							checked={filteredEntryIds.length > 0 && filteredEntryIds.every(id => selectedEntries.includes(id))}
							onChange={(e) => handleSelectAll(e.target.checked)}
						/>
						<span>Select All Filtered ({filteredEntryIds.length})</span>
					</label>
				</div>
				<div>
					<button
						className="btn !text-xs !py-1 !px-3"
						onClick={() => {
							if (getFilteredSelectedCount() === 0) {
								toast.warning("Please select at least one entry to clone")
								return
							}
							setCloneModal(true)
						}}
						disabled={getFilteredSelectedCount() === 0}>
						Clone ({getFilteredSelectedCount()})
					</button>
					<button
						className="btn !bg-red-500 !text-xs !py-1 !px-3 ml-2"
						onClick={() => {
							if (getFilteredSelectedCount() === 0) {
								toast.warning("Please select at least one entry to delete")
								return
							}
							setDeleteModal(true)
						}}
						disabled={getFilteredSelectedCount() === 0}>
						Delete ({getFilteredSelectedCount()})
					</button>
					<span className="ml-2 inline-block">
						<DeleteUndoButton />
					</span>
				</div>
			</div>
			<FullTable
				data={entries}
				columns={columns}
				classNames={"small"}
				pageSize={50}
				onFilteredRowsChange={handleFilteredRowsChange}
			/>
			<Modal open={editModal} onClose={() => setEditModal(false)} center>
				<h2>Edit Entry</h2>
				<div className="flex justify-end">
					<span
						className="btn !bg-red-500 !text-xs ml-5"
						onClick={async () => {
							if (!confirmDelete) {
								setConfirmDelete(!confirmDelete)
							} else {
								let resp = await patch({ id: editData.id, action: "deleteEntry", tags: ["entries"] })
								setEditModal(false)
								setConfirmDelete(false)
								toast.success(resp?.data?.message)
							}
						}}>
						{confirmDelete ? "Confirm Delete?" : "Delete Entry"}
					</span>
				</div>
				<DisplayForm />
			</Modal>
			<Modal
				open={cloneModal}
				onClose={() => {
					setCloneModal(false)
					setSelectedQuarter(null)
				}}
				center
				styles={{
					modal: {
						overflow: 'visible'
					}
				}}>
				<h2>Clone Entries</h2>
				<div className="mb-5">
					<p className="text-sm mb-3">Clone {getFilteredSelectedCount()} selected entries to a different quarter</p>
					<label className="block mb-2">Select Target Quarter:</label>
					<Select
						name="targetQuarter"
						value={selectedQuarter}
						onChange={(_, v) => setSelectedQuarter(v)}
						options={helpers.buildQuarters()}
					/>
				</div>
				<div className="flex gap-3 justify-end">
					<button
						className="btn !bg-gray-400"
						onClick={() => {
							setCloneModal(false)
							setSelectedQuarter(null)
						}}>
						Cancel
					</button>
					<button
						className="btn"
						onClick={async () => {
							if (!selectedQuarter) {
								toast.warning("Please select a target quarter")
								return
							}
							const filteredSelectedIds = getFilteredSelectedEntries()
							if (filteredSelectedIds.length === 0) {
								toast.warning("No entries selected in current view")
								return
							}
							try {
								const resp = await patch({
									action: "cloneEntries",
									entryIds: filteredSelectedIds,
									targetQuarter: selectedQuarter,
									tags: ["entries"]
								})

								if (resp?.data?.status === 200) {
									toast.success(resp.data.message || `Successfully cloned ${filteredSelectedIds.length} entries`)
									setCloneModal(false)
									setSelectedQuarter(null)
								} else if (resp?.error) {
									toast.error(resp.error?.data?.message || "Error cloning entries")
								} else {
									toast.error(resp?.data?.message || "Error cloning entries")
								}
							} catch (error) {
								console.error("Clone error:", error)
								toast.error("An error occurred while cloning entries")
							}
						}}
						disabled={!selectedQuarter}>
						Confirm Clone
					</button>
				</div>
			</Modal>
			<Modal
				open={deleteModal}
				onClose={() => {
					setDeleteModal(false)
				}}
				center
				styles={{ modal: { width: "520px", maxWidth: "92vw" } }}>
				<h2>Delete Entries</h2>
				<div className="mb-4">
					<p className="text-sm mb-3">
						Delete {getFilteredSelectedCount()} selected entries from the current filtered view.
					</p>
					<div className="max-h-[240px] overflow-auto border border-neutral-200">
						<table className="w-full">
							<tbody>
								{getFilteredSelectedEntryRows().map((entry) => (
									<tr key={entry.id} className="child:text-xs child:py-1 child:px-2">
										<td className="w-[72px] font-bold">ID {entry.id}</td>
										<td>{entryDeleteLabel(entry)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
				<div className="flex gap-3 justify-end">
					<button
						className="btn !bg-gray-400"
						onClick={() => {
							setDeleteModal(false)
						}}>
						Cancel
					</button>
					<button
						className="btn !bg-red-500"
						onClick={handleBulkDelete}
						disabled={bulkDeleting}>
						Confirm Delete
					</button>
				</div>
			</Modal>
		</div>
	)
}

export default Entry
